import React from 'react';
import styled from 'styled-components';
import { Body7 } from '@ui';
import { formatKnowableAt, formatScore, formatScoreDelta, formatUtcDate } from './format';
import { plotSeries, seriesDomains } from './scoreSeries';
import { seriesLineCss } from './seriesLineCss';

// The same geometry the cell uses, at a size that can carry axes. Deliberately not chart.js, which
// is already a dependency: each canvas is a live chart instance, jsdom implements none and
// setupTests.js installs no canvas mock, and a second drawing engine would let the panel and the
// cell disagree about what a gap looks like. One module draws both.
//
// Exported as DEFAULTS rather than kept private, because the component now takes them as props.
// The viewBox plus width:100%/height:auto already made this fit any container without a source
// edit — what was locked was the aspect ratio, the 46px left inset tuned to four y-labels, and the
// three x-labels that fit at this width. "Make it a compact inline preview" meant editing module
// constants; now it is a prop.
export const CHART_BOX = { width: 640, height: 260, padding: { top: 16, right: 16, bottom: 30, left: 46 } };

// Air above and below the line. The cell passes none — its box is 26px and every pixel is signal —
// but a chart with points on its frame reads as clipped.
//
// Careful with this one: padding widens the DOMAIN by a fraction of the RANGE, so the bigger the
// climb next to the floor the further it reaches — 1 -> 10 at 0.18 lands the axis at -0.62.
// seriesDomains clamps that at zero, so an all-positive series never gains a zero line and a strip
// of negative scale it never reached. Widening this prop is safe; removing that clamp is not.
export const PAD_FRACTION = 0.18;

export const Y_TICKS = 4;

const Wrapper = styled.div`
  position: relative;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 8px;
  padding: 8px 4px 2px;
`;

const Plot = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  touch-action: none;

  .grid-line {
    stroke: ${({ theme }) => theme.themeColors.lowContrastBorder};
    stroke-width: 1;
  }

  .zero-line {
    stroke: ${({ theme }) => theme.themeColors.border};
    stroke-width: 1;
  }

  .axis-text {
    fill: ${({ theme }) => theme.themeColors.secondaryText};
    font-size: 10px;
  }

  ${seriesLineCss}

  .crosshair {
    stroke: ${({ theme }) => theme.themeColors.text};
    stroke-width: 1;
    opacity: 0.35;
  }

  .cursor-dot {
    fill: ${({ theme }) => theme.themeColors.text};
  }
`;

const Tip = styled.div`
  position: absolute;
  z-index: 2;
  max-width: 280px;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.text};
  pointer-events: none;
  transform: translate(-50%, -100%);
`;

const Empty = styled.div`
  padding: 24px 8px;
`;

// One company's occasions, plotted. A gap is a break with a tick at its instant — never a point at
// zero, which would turn "this occasion scored nothing" into "this company scored nothing".
export const ScoreHistoryChart = ({
  series,
  box = CHART_BOX,
  padFraction = PAD_FRACTION,
  yTickCount = Y_TICKS,
  interactive = true,
}) => {
  const [cursor, setCursor] = React.useState(null);
  const svgRef = React.useRef(null);

  const { domains, plot } = React.useMemo(() => {
    const scoped = seriesDomains(series?.rows || [], { padFraction });
    return { domains: scoped, plot: plotSeries(series, scoped, box) };
  }, [series, box, padFraction]);

  // Nothing plotted is a real state — a company every occasion of which went unscored — and it gets
  // a sentence rather than an empty frame, which reads as a broken chart.
  if (!series || !series.scored.length) {
    return (
      <Wrapper>
        <Empty>
          <Body7 isLowContrast>
            No occasion of this company scored in this read, so there is nothing to plot. The occasions are listed
            below.
          </Body7>
        </Empty>
      </Wrapper>
    );
  }

  const { plotBox } = plot;

  const yTicks = Array.from(
    { length: yTickCount + 1 },
    (_, i) => domains.y0 + ((domains.y1 - domains.y0) * i) / yTickCount
  );
  // First, middle and last occasion. Three labels is what fits without collision at this width, and
  // the ends are the two a reader actually checks.
  const xTickRows = [
    series.rows[0],
    series.rows[Math.floor(series.rows.length / 2)],
    series.rows[series.rows.length - 1],
  ]
    .filter(Boolean)
    .filter((row, i, list) => list.indexOf(row) === i);

  // `interactive` short-circuits the pointer rather than conditionally rendering the handler, so a
  // static variant is one prop instead of a fork of this file. The SVG keeps its role="img" and its
  // accessible name either way — the chart is readable without the cursor, which is the point.
  const handleMove = (event) => {
    if (!interactive) return;
    const node = svgRef.current;
    if (!node || !plot.points.length) return;
    const rect = node.getBoundingClientRect();
    // jsdom — and a chart that hasn't been laid out yet — reports a zero-width box, which would make
    // every position NaN. No box, no cursor.
    if (!rect.width) return;
    const at = ((event.clientX - rect.left) / rect.width) * box.width;
    const nearest = plot.points.reduce(
      (best, point) => (!best || Math.abs(point.x - at) < Math.abs(best.x - at) ? point : best),
      null
    );
    setCursor(nearest);
  };

  return (
    <Wrapper>
      <Plot
        ref={svgRef}
        viewBox={`0 0 ${box.width} ${box.height}`}
        role="img"
        aria-label={`Score by occasion for ${series.rows[0].symbol || `company ${series.companyId}`}, ${formatUtcDate(
          series.from
        )} to ${formatUtcDate(series.to)}.`}
        onMouseMove={interactive ? handleMove : undefined}
        onMouseLeave={interactive ? () => setCursor(null) : undefined}
        data-testid="score-history-chart"
      >
        {yTicks.map((value, i) => (
          <g key={`y-${i}`}>
            <line className="grid-line" x1={plotBox.left} x2={plotBox.right} y1={plot.y(value)} y2={plot.y(value)} />
            <text className="axis-text" x={plotBox.left - 8} y={plot.y(value) + 3} textAnchor="end">
              {formatScore(Math.round(value * 100) / 100)}
            </text>
          </g>
        ))}

        {/* Only when the series crosses it. A tone score can be negative, and that boundary means
            something no grid line does — but drawn on a series that never crosses it, it would only
            add a rule the reader has to account for. */}
        {domains.y0 < 0 && domains.y1 > 0 && (
          <line
            className="zero-line"
            data-testid="chart-zero-line"
            x1={plotBox.left}
            x2={plotBox.right}
            y1={plot.y(0)}
            y2={plot.y(0)}
          />
        )}

        {xTickRows.map((row) => (
          <text
            key={`x-${row.knowableAt}`}
            className="axis-text"
            x={plot.x(new Date(row.knowableAt).getTime())}
            y={box.height - 10}
            textAnchor="middle"
          >
            {formatUtcDate(row.knowableAt)}
          </text>
        ))}

        {plot.gaps.map((gap, i) => (
          <line
            key={`gap-${i}`}
            className="series-gap"
            data-testid="chart-gap"
            x1={gap.x}
            x2={gap.x}
            y1={plotBox.top}
            y2={plotBox.bottom}
          />
        ))}

        {plot.paths.map((d, i) => (
          <path key={`segment-${i}`} className="series-line" data-testid="chart-segment" d={d} />
        ))}

        {plot.points.map((point) => (
          <circle key={point.row.key} className="series-point" cx={point.x} cy={point.y} r={4} />
        ))}

        {cursor && (
          <>
            <line
              className="crosshair"
              data-testid="chart-crosshair"
              x1={cursor.x}
              x2={cursor.x}
              y1={plotBox.top}
              y2={plotBox.bottom}
            />
            <circle className="cursor-dot" data-testid="chart-cursor-dot" cx={cursor.x} cy={cursor.y} r={4.5} />
          </>
        )}
      </Plot>

      {cursor && (
        <Tip
          role="status"
          style={{ left: `${(cursor.x / box.width) * 100}%`, top: `${(cursor.y / box.height) * 100}%` }}
        >
          <Body7 bold>{formatKnowableAt(cursor.row.knowableAt)}</Body7>
          <Body7>
            {formatScore(cursor.row.score)}
            {cursor.row.scoreDelta === null || cursor.row.scoreDelta === undefined
              ? ''
              : ` · ${formatScoreDelta(cursor.row.scoreDelta)} vs previous occasion`}
          </Body7>
        </Tip>
      )}
    </Wrapper>
  );
};
